import $ from 'jquery';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ChevronDown } from 'phx-core';

import {
  Component,
  ElementRef,
  HostBinding,
  OnInit,
  ViewChild,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';

import { AdaChatbotComponent } from '@app/common-components/ada-chatbot.component';
import { LOGO_TYPES } from '@app/common-components/logo.component';

import { Session } from '@services/session';
import {
  SidebarService,
  INavigation,
} from '@services/sidebar.service';
import { GroupsFilter } from '@services/groups-filter';
import { FleetGrowthFeatureFlagService } from '@services/feature-flags/growth';
import { AssetMonitoringFeatureFlagService } from '@app/services/feature-flags/asset-monitoring';
import { AdminFeatureFlagService } from '@app/services/feature-flags/admin';
import { FleetCardsFeatureFlagService } from '@app/services/feature-flags/fleet-cards';
import { FleetCardsDataService } from '@app/services/fleet-cards-data';
import { DashboardSettings } from '@app/services/dashboard-settings';
import { PendoAnalytics } from '@app/services/pendo-analytics';
import { LocalStorage } from '@app/services/local-storage';
import { DocumentsService } from '@app/admin/fleet-card/card-application/documents.service';
import { FeatureUpgradeService } from '@misc/upsell/upgrade.service';
import { FleetCardsOffersService } from '@fleet-cards/offers/service';

import { isMobileView } from '@app/lib/screen-sizes';
import { getShopUrl } from '@lib/utils';
import { CurrencyPipe } from '@angular/common';
import { normaliseMicros } from '@app/admin/fleet-card/shared/utils';

import {
  setNextDisplayDate,
  setUpChecks,
} from '@app/lib/fleet-card/card-application-nudge-experiment';

import { IFleetCardCompany } from '@app/admin/fleet-card/shared/fleet-cards-waitlist/fleet-cards-waitlist.interface';

import {
  INavItem,
  ISidebarPopoverConfig,
  FLEET_CARDS_NOTIFICATION_POPOVER_TEXT_CONFIG,
  IButtonConfig,
  FLEET_CARDS_NOTIFICATION_POPOVER_EVENTS,
  ADMIN_NAV_GROUPS,
  ADMIN_NAV_ITEMS,
  PHX_ICON_NAMES_MAP,
  ADMIN_NAV_GROUPS_MAP,
} from './sidebar.constants';
import {
  FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG,
  FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE,
  PLAID_URL,
  NUDGE_POPOVER_DELAY,
  ITcvRewardsObject,
} from '@app/lib/fleet-card/card-application-nudge-experiment';
import { FLEET_CARD_APPLICATION_STATUS } from '@app/dashboard/fleet-cards/fleet-card-status';
import { CARD_APPLICATION_STATUS } from '@app/admin/fleet-card/main.constants';
import { UPSELL_CAMPAIGN_NAMES } from '@misc/upsell/upsell-config';
import { ECOMM_ELIGIBLE_ENTITIES } from '@app/admin/shop/ecomm-eligible-entities';

import 'style-loader!./sidebar.component.deep.scss';

@Component({
  selector: 'new-sidebar',
  styleUrls: ['./sidebar.component.scss', '../../styles/shared/perfect-scroll.scss'],
  templateUrl: './sidebar.component.html',
  providers: [DocumentsService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewSidebarComponent implements OnInit, OnDestroy {
  @ViewChild('adaChatbot', { static: false }) public adaChatbot: AdaChatbotComponent;
  @ViewChild('topNav', { static: false }) public set topNav(el: ElementRef) {
    if (el) this.bindScroll(el);
  }
  @HostBinding('class') public className: string = 'ui fixed borderless sidebar top-level';
  @HostBinding('class.light-theme') @Input() public isLightTheme: boolean;
  @HostBinding('class.expand-sidebar') @Input() public isSideBarMaximized: boolean;
  public sidebarNav: INavigation = null;
  public currentActiveLinkIndex: number;
  public shouldHideQuickAction: boolean = false;
  public userHasEmail: boolean;
  public isAdminSidebar: boolean = false;
  public readonly phxIconNames: Record<keyof typeof PHX_ICON_NAMES_MAP, string> = PHX_ICON_NAMES_MAP;
  public readonly chevronDownIcon: string = ChevronDown;
  public adminNavGroups: typeof ADMIN_NAV_GROUPS = ADMIN_NAV_GROUPS;
  public adminNavGroupsTitles: typeof ADMIN_NAV_GROUPS_MAP = ADMIN_NAV_GROUPS_MAP;
  public currentFeatureLabel: string = '';
  public adminNavItems: Record<ADMIN_NAV_GROUPS, INavItem[]>;
  public adminSidebarCurrentIndexes: Record<ADMIN_NAV_GROUPS, number> = {
    [ADMIN_NAV_GROUPS.ORGANIZATION]: null,
    [ADMIN_NAV_GROUPS.PLATFORM]: null,
    [ADMIN_NAV_GROUPS.PRODUCTS]: null,
  };
  public adminSidebarStates: Record<ADMIN_NAV_GROUPS, boolean> = {
    [ADMIN_NAV_GROUPS.ORGANIZATION]: true,
    [ADMIN_NAV_GROUPS.PLATFORM]: true,
    [ADMIN_NAV_GROUPS.PRODUCTS]: true,
  };
  public isInstallerRolePermissionEnabled: boolean;
  public logoTypes: typeof LOGO_TYPES = LOGO_TYPES;
  public groupsFilterDisabled$: Observable<boolean>;
  public groupsFilterDisabledTooltip: string = $localize`The group filter is unavailable in reports`;
  public isChatbotAvailable: boolean;
  public isChatbotSingleClickUXEnabled: boolean;
  public popover?: ISidebarPopoverConfig;
  public showPopover: boolean = false;
  public isFleetCardRoute: boolean = false;
  public readonly shopUrl: string = getShopUrl();
  public isLogoBorderVisible: boolean = false;
  public loadDeferComponents: boolean = false;
  private routerSubscription: Subscription;
  public potentialSavings: number = 0;
  public rewardDetails: ITcvRewardsObject;
  public documentsUploaded: boolean = false;
  public company: IFleetCardCompany;
  public ccmdStatus: string;
  public bankAccountLinked: boolean;
  public fleetCardApplicationfirstVisitedDate: string;
  public fleetCardApplicationNudgeDismissCount: number;
  public fleetCardApplicationNudgeNextDisplayDate: string;
  public messageType: string;
  public fleetCardsApplicationNudgeMessageType: typeof FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE
    = FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE;
  public plaidUrl: string = PLAID_URL;
  private $topNav;
  private navigationHash: { [key: string]: number } = {};
  private isHFTGroupFilterInPageHeaderEnabled: boolean;
  private popoverIdentifierKey: string = 'popover-identifier';
  private popoverDismissalCountKey: string = 'popover-dismissal-count';
  private isFleetCardsPromoOffersEnabled: boolean;
  public isUpsellVisible: boolean;
  public isFleetCardShowCardsTabSidebarEnabled: boolean = false;

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    public session: Session,
    private sidebarService: SidebarService,
    private amFeatureFlagSession: AssetMonitoringFeatureFlagService,
    private fcFeatureFlagService: FleetCardsFeatureFlagService,
    private featureUpgradeService: FeatureUpgradeService,
    private currencyPipe: CurrencyPipe,
    private pendo: PendoAnalytics,
    private adminFeatureFlagService: AdminFeatureFlagService,
    private dashboardSettings: DashboardSettings,
    private fleetCardsDataService: FleetCardsDataService,
    private localStorage: LocalStorage,
    private documentsService: DocumentsService,
    private groupsFilterService: GroupsFilter,
    private fgFeatureFlagService: FleetGrowthFeatureFlagService,
    private fleetCardsOfferService: FleetCardsOffersService,
  ) {
    this.userHasEmail = this.session.userHasEmail();
    this.isChatbotAvailable = this.fgFeatureFlagService.isChatbotEnabled();
    this.isChatbotSingleClickUXEnabled = this.fgFeatureFlagService.isChatbotSingleClickUXEnabled();
    this.isInstallerRolePermissionEnabled = this.amFeatureFlagSession.isInstallerRolePermissionEnabled();
    this.groupsFilterDisabled$ = this.groupsFilterService.groupsComponentDisable$;
    this.isHFTGroupFilterInPageHeaderEnabled = this.adminFeatureFlagService.isGroupFilterInNewHeaderEnabled();
    this.isFleetCardsPromoOffersEnabled = this.fcFeatureFlagService.isFleetCardsPromoOffersEnabled();
    this.isFleetCardShowCardsTabSidebarEnabled = this.fcFeatureFlagService.isFleetCardShowCardsTabSidebarEnabled();
  }

  public ngOnInit() {
    const shopUtm = ADMIN_NAV_ITEMS.find(item => item.name === 'shop_utm');
    const shopExp = ADMIN_NAV_ITEMS.find(item => item.name === 'shop_exp');

    const utmParams: any = this.featureUpgradeService.getDefaultUTMParams(UPSELL_CAMPAIGN_NAMES.shop_evergreen_upsell);
    shopUtm.link = shopUtm.link + '?' + new URLSearchParams(utmParams).toString();

    const showUpsell = this.session.getEcommEligibility() === ECOMM_ELIGIBLE_ENTITIES.DEFAULT;
    shopExp.link = showUpsell ? '/admin/shop' : getShopUrl();
    shopExp.isOutsideLink = !showUpsell;

    if (performance.getEntriesByName('window_loaded')) {
      this.loadDeferComponents = true;
    } else {
      setTimeout(() => {
        if (!this.loadDeferComponents) {
          this.loadDeferComponents = true;
          this.cdr.detectChanges();
        }
      }, 20_000);
      window.addEventListener('load', () => {
        this.loadDeferComponents = true;
        this.cdr.detectChanges();
      });
    }
    this.sidebarService.navListener.subscribe((nav: INavigation) => {
      this.sidebarNav = nav;
      this.buildNavigationHash();
      this.shouldHideQuickAction = nav.sidebarPrimaryLink === 'Account' || this.session.isDriver();
      this.isAdminSidebar = nav.sidebarPrimaryLink === 'Admin';
      this.currentFeatureLabel = (nav.navItems && nav.navItems[0]?.label) || '';
      if (this.isAdminSidebar) {
        this.adminNavItems = {
          [ADMIN_NAV_GROUPS.ORGANIZATION]: nav.navItems?.filter((navItem: INavItem) => {
            return navItem?.group === ADMIN_NAV_GROUPS.ORGANIZATION;
          }),
          [ADMIN_NAV_GROUPS.PLATFORM]: nav.navItems?.filter((navItem: INavItem) => navItem?.group === ADMIN_NAV_GROUPS.PLATFORM),
          [ADMIN_NAV_GROUPS.PRODUCTS]: nav.navItems?.filter((navItem: INavItem) => navItem?.group === ADMIN_NAV_GROUPS.PRODUCTS),
        };
      }
      this.cdr.detectChanges();
    });

    this.routerSubscription = this.router.events
      .pipe(
        filter(e => e instanceof NavigationStart || e instanceof NavigationCancel || e instanceof NavigationEnd),
      )
      .subscribe(this.onRouteChange);

    if (this.fcFeatureFlagService.isCardsActivationReminderEnabled() && this.session.isCardManagementManageEnabled()) {
      this.checkForPopover();
    }
    if (this.session.isAdmin()
      && !isMobileView()
      && this.session.getFleetCardApplicationStatus() === FLEET_CARD_APPLICATION_STATUS.UPSELL
      && this.isFleetCardShowCardsTabSidebarEnabled
    ) {
      this.checkForCardApplicationNudgePopover();
    }

    this.handleChatbotToggles();

    if (this.isFleetCardsPromoOffersEnabled) {
      this.checkFleetCardOffersAndSetNotificationDot();
      this.listenForChangeDetection();
    }
  }

  public ngOnDestroy() {
    this.routerSubscription.unsubscribe();
  }

  public get isAdminAccessAllowed(): boolean {
    return this.session.userPermissions.hasAdministrativePermission();
  }

  public get isDashboardAccessAllowed(): boolean {
    return this.session.userPermissions.hasDashboardPermission();
  }

  public get isDashboardAdminSwitcherEnabled(): boolean {
    return this.isInstallerRolePermissionEnabled
      ? this.isAdminAccessAllowed && this.isDashboardAccessAllowed
      : this.isAdminAccessAllowed;
  }

  public get showSidebarFilter(): boolean {
    return this.session.isDispatcher() && this.sidebarNav.hasGroups && !this.isHFTGroupFilterInPageHeaderEnabled;
  }

  public accordionStateChange(accordionBlock: ADMIN_NAV_GROUPS): void {
    this.adminSidebarStates[accordionBlock] = !this.adminSidebarStates[accordionBlock];
  }

  public bindScroll(topNav: ElementRef): void {
    this.$topNav = $(topNav.nativeElement);
  }

  public setCurrentActiveLink(url: string): void {
    this.currentActiveLinkIndex = -1;

    if (this.isAdminSidebar) {
      Object.values(this.adminNavGroups).forEach((navGroup: ADMIN_NAV_GROUPS) => {
        const activeTabIndex = this.adminNavItems[navGroup]?.findIndex(item => url.startsWith(item.link));
        this.adminSidebarCurrentIndexes[navGroup] = activeTabIndex;
      });
    } else {
      const currentNavItem: INavItem = this.sidebarNav.navItems.find(item => url.startsWith(item.link));
      this.currentActiveLinkIndex = currentNavItem ? this.navigationHash[currentNavItem.link] : this.currentActiveLinkIndex;
    }

    this.cdr.detectChanges();
  }

  public onSidebarItemClick(item: INavItem): void {
    if (this.showPopover && item.name === this.popover?.key) {
      this.showPopover = false;
    }

    this.currentFeatureLabel = item.name;

    if (item.name === 'shop') {
      this.pendo.trackEvent('Shop Icon - Fleet Dashboard Sidebar Menu - Click', {
        companyId: this.session.getCompany().id,
        userEmail: this.session.getUserEmail(),
      });
    }

    if (item.name === 'fleetCards') {
      this.localStorage.setFleetCardNotificationDot(false);
      this.sidebarService.setFleetCardNotificationDot();
    }
  }

  private checkFleetCardOffersAndSetNotificationDot(): void {
    if (this.session.isDriver()) return;
    this.fleetCardsOfferService.getNewOffersCount().subscribe((newOfferCount: number) => {
      if (newOfferCount > 0) {
        this.sidebarService.setFleetCardNotificationDot(true, true);
      }
    });
  }

  public chatbotUnavailable(): void {
    this.isChatbotAvailable = false;
  }

  public onScroll(event: Event): void {
    const MIN_SCROLL_POSITION = 10;
    const scrollPosition = (event.target as HTMLElement).scrollTop;
    this.isLogoBorderVisible = scrollPosition > MIN_SCROLL_POSITION;
  }

  public handleChatbotToggles(): void{
    this.sidebarService.toggleChatBot.subscribe(() => {
      if (!this.adaChatbot.isChatBotOpen){
        this.adaChatbot.toggleChatbot();
      }
    });
  }

  private buildNavigationHash(): void {
    this.navigationHash = {};
    this.sidebarNav.navItems.forEach((value: INavItem, index: number) => {
      this.navigationHash[value.link] = index;
    });
  }

  private checkIfFleetCardRoute(url: string): void {
    if (url.includes('/fleet-cards/')) {
      this.isFleetCardRoute = true;
    } else this.isFleetCardRoute = false;
  }

  private hidePopoverIfSettingsRoute(url: string): void {
    if (url.match('/settings')) {
      this.showPopover = false;
    }
  }

  private onRouteChange = (event: NavigationStart | NavigationCancel | NavigationEnd): void => {
    let url: string = event.url;
    this.checkIfFleetCardRoute(url);
    if (event instanceof NavigationEnd) this.hidePopoverIfSettingsRoute(url);
    if (event instanceof NavigationCancel) url = this.router.url;
    else if (event instanceof NavigationEnd) url = event.urlAfterRedirects;
    this.setCurrentActiveLink(url);
  }

  private hasReceivedCards(company: IFleetCardCompany): boolean {
    return !!company.shippingDetails?.hasReceivedCards;
  }

  private checkForPopover(): void {
    this.getCompanyData().subscribe((company: IFleetCardCompany) => {
      if (this.isCardApplicationApproved(company) &&
        this.isValidForFeatureCutOffDates(company.cardApplication?.creditApprovedAt) &&
        this.hasNotActivatedAnyCards(company) &&
        !isMobileView()) {
        const activateBtn = {
          text: $localize`Activate now`,
          onClick: () => {
            this.pendo.trackEvent(FLEET_CARDS_NOTIFICATION_POPOVER_EVENTS.activateNowBtnClicked, {});
            this.router.navigate(['/admin/fleet-cards/card-management/activate-card'],
              {
                state: { redirectFrom: 'activation-reminder' },
              });
          },
        };
        const exploreBenefitsBtn = {
          text: $localize`Explore benefits`,
          onClick: () => {
            this.pendo.trackEvent(FLEET_CARDS_NOTIFICATION_POPOVER_EVENTS.exploreBenefitsBtnClicked, {});
            this.router.navigate(['/fleet-cards/benefits']);
          },
        };
        const onDismiss = () => {
          this.pendo.trackEvent(FLEET_CARDS_NOTIFICATION_POPOVER_EVENTS.dismissClicked, {});
        };

        if (this.isPastApprovedCustomer(company.cardApplication?.creditApprovedAt)) {
          this.triggerPopover(
            FLEET_CARDS_NOTIFICATION_POPOVER_TEXT_CONFIG.pastApprovedCustomers.header(company.approvedCardCount.toString()),
            FLEET_CARDS_NOTIFICATION_POPOVER_TEXT_CONFIG.pastApprovedCustomers.content,
            activateBtn, exploreBenefitsBtn, onDismiss);
        } else {
          if (this.hasReceivedCards(company)) {
            this.triggerPopover(FLEET_CARDS_NOTIFICATION_POPOVER_TEXT_CONFIG.cardsReceived.header,
              FLEET_CARDS_NOTIFICATION_POPOVER_TEXT_CONFIG.cardsReceived.content(
                this.formatMicros(company.maxCreditLimitInMicros),
                company.approvedCardCount.toString()),
              activateBtn, undefined, onDismiss);
          } else {
            this.triggerPopover(FLEET_CARDS_NOTIFICATION_POPOVER_TEXT_CONFIG.cardsShipped.header,
              FLEET_CARDS_NOTIFICATION_POPOVER_TEXT_CONFIG.cardsShipped.content(
                this.formatMicros(company.maxCreditLimitInMicros),
                company.approvedCardCount.toString()),
              exploreBenefitsBtn, undefined, onDismiss);
          }
        }
      }
    });
  }

  private checkForCardApplicationNudgePopover(): void {
    if ((!this.fcFeatureFlagService.isFleetCardsFleetBundleExperimentEnabled() &&
      this.fcFeatureFlagService.getFleetCardsApplicationNudgeExperimentValue() !== 'popover') ||
      (this.fcFeatureFlagService.getFleetCardsApplicationNudgeExperimentValue() === 'no_upsell') &&
      this.session.isTrialCustomer()) {
      return;
    }

    setUpChecks(
      'popover',
      this.fcFeatureFlagService,
      this.localStorage,
      this.documentsService,
      this.fleetCardsDataService,
      (documentsUploaded: boolean) => this.documentsUploaded = documentsUploaded,
      (company: IFleetCardCompany) => this.company = company,
      (bankAccountLinked: boolean) => this.bankAccountLinked = bankAccountLinked,
      (messageType: string) => this.messageType = messageType,
      (potentialSavings: number) => this.potentialSavings = potentialSavings,
      () => setTimeout(() => this.showCardApplicationNudgePopover(), NUDGE_POPOVER_DELAY),
      (rewardDetails: ITcvRewardsObject) => this.rewardDetails = rewardDetails,
    );
  }

  public showCardApplicationNudgePopover(): void {
    this.pendo.trackEvent('Experiment - Application Nudge - Exposure - Popover', {
      message_type: this.messageType,
    });

    const actionBtn = (btnText: string) => {
      return {
        text: btnText,
        onClick: () => {
          this.pendo.trackEvent('Experiment - Application Nudge - Popover - Click', {
            message_type: this.messageType,
          });
          setNextDisplayDate(this.localStorage, this.messageType);
          this.router.navigate(['/admin/fleet-cards/card-application']);
        },
      };
    };

    const onDismiss = () => {
      setNextDisplayDate(this.localStorage, this.messageType);
      this.pendo.trackEvent('Experiment - Application Nudge - Popover - Dismiss', {
        message_type: this.messageType,
      });
      if (!this.router.url.includes('/fleet-cards/')) {
        this.localStorage.setFleetCardNotificationDot(true);
        this.sidebarService.setFleetCardNotificationDot();
      }
    };

    const potentialSavings: string = this.currencyPipe.transform(this.potentialSavings, 'USD', 'symbol', '1.0-0');
    const rewardValue: string = this.currencyPipe.transform(this.rewardDetails?.rewardValue, 'USD', 'symbol', '1.0-0');
    const rewardPercentage: number = this.rewardDetails?.rewardPercentage;
    const daysRemainingUntilExpiry: number = this.rewardDetails?.daysRemainingUntilExpiry;

    if (this.messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.linkBankAccount) {
      this.triggerPopoverV2(
        FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.linkBankAccount.header,
        FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.linkBankAccount.body,
        undefined,
        actionBtn(FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.linkBankAccount.btnText),
        undefined,
        onDismiss,
      );
    } else if (this.messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.potentialSavingsFinishApplication) {
      this.triggerPopoverV2(
        FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.potentialSavingsFinishApplication.header,
        FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.potentialSavingsFinishApplication.body(potentialSavings),
        undefined,
        actionBtn(FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.potentialSavingsFinishApplication.btnText),
        undefined,
        onDismiss,
      );
    } else if (this.messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.fleetBundle3DMessage) {
      this.triggerPopoverV2(
        FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.fleetBundle3DMessage.header(rewardValue),
        FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.fleetBundle3DMessage.body(rewardValue),
        undefined,
        actionBtn(FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.fleetBundle3DMessage.btnText),
        undefined,
        onDismiss,
      );
    } else if (this.messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.fleetBundle15DMessage) {
      this.triggerPopoverV2(
        FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.fleetBundle15DMessage.header(rewardPercentage, daysRemainingUntilExpiry),
        FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.fleetBundle15DMessage.body(rewardPercentage),
        FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.fleetBundle15DMessage.subBody(rewardValue),
        actionBtn(FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.fleetBundle15DMessage.btnText),
        undefined,
        onDismiss,
      );
    } else if (this.messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.fleetBundle45DMessage) {
      this.triggerPopoverV2(
        FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.fleetBundle45DMessage.header(rewardPercentage),
        FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.fleetBundle45DMessage.body(rewardValue),
        FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.fleetBundle45DMessage.subBody,
        actionBtn(FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.fleetBundle45DMessage.btnText),
        undefined,
        onDismiss,
      );
    } else { // upsell
      this.triggerPopoverV2(
        FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.potentialSavingsApplyNow.header(potentialSavings),
        FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.potentialSavingsApplyNow.body,
        undefined,
        actionBtn(FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.potentialSavingsApplyNow.btnText),
        undefined,
        onDismiss,
      );
    }
  }

  public popoverDismissed(): void {
    const currentDismissalCount = this.getPopoverDismissalCount();
    console.log('popover dismissed', currentDismissalCount);
    if (currentDismissalCount + 1 > 2) {
      this.setPopoverIdentifier(JSON.stringify(this.popover));
    } else {
      this.setPopoverDismissalCount(
        currentDismissalCount ? currentDismissalCount + 1 : 1);
    }
    this.popover?.onDismiss();
    this.showPopover = false;
  }

  public popoverPrimaryBtnClicked(): void {
    this.setPopoverIdentifier(JSON.stringify(this.popover));
    this.popover?.primaryBtn.onClick();
    this.showPopover = false;
  }

  public popoverSecondaryBtnClicked(): void {
    this.setPopoverIdentifier(JSON.stringify(this.popover));
    this.popover?.secondaryBtn.onClick();
    this.showPopover = false;
  }

  private triggerPopover(header: string,
    content: string,
    primaryBtn?: IButtonConfig,
    secondaryBtn?: IButtonConfig, onDismiss?: () => void)
    : void {
    const config: ISidebarPopoverConfig = {
      header,
      key: 'fleetCards',
      content,
      intent: 'positive',
      primaryBtn,
      secondaryBtn,
      onDismiss,
    };

    const currentPopoverIdentifier: string = this.getPopoverIdentifier();
    const newPopoverConfig: string = JSON.stringify(config);

    if (currentPopoverIdentifier === newPopoverConfig) {
      return;
    }

    if (currentPopoverIdentifier && currentPopoverIdentifier !== newPopoverConfig) {
      this.setPopoverDismissalCount(0);
      this.setPopoverIdentifier(null);
    }

    if (this.isFleetCardRoute) return;

    this.popover = config;
    this.showPopover = true;
    this.pendo.trackEvent(FLEET_CARDS_NOTIFICATION_POPOVER_EVENTS.viewEvent, {});
    this.cdr.detectChanges();
  }

  public getCompanyData(): Observable<IFleetCardCompany> {
    return this.fleetCardsDataService.getCompanyCardMetadataStream();
  }

  public openOutsideLinkInNewTab(url: string): void {
    window.open(url, '_blank');
  }

  private triggerPopoverV2(
    header: string,
    content: string,
    subContent: string,
    primaryBtn?: IButtonConfig,
    secondaryBtn?: IButtonConfig,
    onDismiss?: () => void)
    : void {
    const config: ISidebarPopoverConfig = {
      header,
      key: 'fleetCards',
      content,
      subContent,
      intent: 'accent',
      primaryBtn,
      secondaryBtn,
      onDismiss,
    };

    this.popover = config;
    this.showPopover = true;
    this.cdr.detectChanges();
  }

  private isPastApprovedCustomer(creditApprovedAt: string): boolean {
    const startDate = new Date('2023-01-01');
    const endDate = new Date('2023-06-14');
    return new Date(creditApprovedAt) > startDate && new Date(creditApprovedAt) < endDate;
  }

  private isValidForFeatureCutOffDates(creditApprovedAt: string): boolean {
    const cutOffDate = new Date('2023-01-01');
    return new Date(creditApprovedAt) > cutOffDate;
  }

  private isCardApplicationApproved(company: IFleetCardCompany): boolean {
    const isApproved: boolean = company.cardApplication?.status === CARD_APPLICATION_STATUS.issued && company.status === 'active';
    if (isApproved && !this.isFleetCardRoute) this.fleetCardsDataService.getCachedAccountOverview();
    return isApproved;
  }

  private hasNotActivatedAnyCards(company: IFleetCardCompany): boolean {
    return company.unactivatedCardCount === company.approvedCardCount;
  }

  private formatMicros(micros: number): string {
    return this.currencyPipe.transform(normaliseMicros(micros), undefined, undefined, '.0');
  }

  private setPopoverDismissalCount(val: number): void {
    this.dashboardSettings.setKey(this.popoverDismissalCountKey, val);
  }

  private getPopoverDismissalCount(): number {
    return Number(this.dashboardSettings.getKey(this.popoverDismissalCountKey));
  }

  private setPopoverIdentifier(val: string): void {
    this.dashboardSettings.setKey(this.popoverIdentifierKey, val);
  }

  private getPopoverIdentifier(): string {
    return this.dashboardSettings.getKey(this.popoverIdentifierKey);
  }

  public handlePlaidLinkClick(): void {
    if (this.messageType !== FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.linkBankAccount) return;
    this.pendo.trackEvent('Experiment - Application Nudge - Popover - Click', {
      message_type: this.messageType,
      plaid_link: true,
    });
    window.open(this.plaidUrl, '_blank');
  }

  private listenForChangeDetection(): void {
    this.sidebarService.triggerChangeDetection$.subscribe(() => this.cdr.detectChanges());
  }
}
